import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import WOW from 'wowjs';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Services from './pages/Services';
import Portfolio from './pages/Portfolio';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import NotFound from './pages/NotFound';
import './App.css';
import './layout-fixes.css';

const RouteEffects: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });

    const animationTimer = window.setTimeout(() => {
      new WOW.WOW({
        live: false
      }).init();
    }, 0);

    return () => window.clearTimeout(animationTimer);
  }, [location.pathname]);

  return null;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const finishLoading = () => {
      window.setTimeout(() => setIsLoading(false), 450);
    };

    if (document.readyState === 'complete') {
      finishLoading();
    } else {
      window.addEventListener('load', finishLoading, { once: true });
    }

    const fallbackTimer = window.setTimeout(() => setIsLoading(false), 1800);

    return () => {
      window.removeEventListener('load', finishLoading);
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  useEffect(() => {
    const cursor = document.getElementById('cursor');
    const cursorRing = document.getElementById('cursor-ring');

    const moveCursor = (e: MouseEvent) => {
      if (cursor && cursorRing) {
        cursor.style.left = e.clientX + 'px';
        cursor.style.top = e.clientY + 'px';
        cursorRing.style.left = e.clientX + 'px';
        cursorRing.style.top = e.clientY + 'px';
      }
    };

    const handleScroll = () => {
      const navbar = document.querySelector('.navbar');
      const topBtn = document.getElementById('back-to-top');
      if (navbar) {
        if (window.scrollY > 50) {
          navbar.classList.add('scrolled');
        } else {
          navbar.classList.remove('scrolled');
        }
      }
      if (topBtn) {
        if (window.scrollY > 300) {
          topBtn.classList.add('visible');
        } else {
          topBtn.classList.remove('visible');
        }
      }
    };

    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    document.addEventListener('mousemove', moveCursor);
    window.addEventListener('scroll', handleScroll);

    const topBtn = document.getElementById('back-to-top');
    if (topBtn) {
      topBtn.addEventListener('click', scrollToTop);
    }

    return () => {
      document.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('scroll', handleScroll);
      if (topBtn) {
        topBtn.removeEventListener('click', scrollToTop);
      }
    };
  }, []);

  return (
    <Router>
      <div className="App">
        {isLoading && (
          <div className="site-loader" role="status" aria-live="polite">
            <div className="site-loader-card">
              <img src={`${process.env.PUBLIC_URL}/wevatar_logo.png`} alt="Wevatar" />
              <div className="site-loader-bar">
                <span />
              </div>
              <p>Preparing your digital experience</p>
            </div>
          </div>
        )}
        <div id="cursor"></div>
        <div id="cursor-ring"></div>
        <button id="back-to-top" className="float-top">↑</button>
        <a
          href="https://wa.me/917896353325"
          className="floating-whatsapp"
          aria-label="Chat with Wevatar on WhatsApp"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19.1 4.9A9.8 9.8 0 0 0 3.7 16.6L2.5 21.5l5-1.3A9.8 9.8 0 0 0 21.8 12a9.7 9.7 0 0 0-2.7-7.1Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M8.6 7.9c.2-.4.4-.5.8-.5h.6c.2 0 .5.1.6.5l.7 1.6c.1.3.1.5-.1.8l-.4.5c-.2.2-.2.4 0 .7.6 1 1.4 1.8 2.5 2.4.3.2.5.2.7 0l.6-.6c.2-.2.5-.3.8-.1l1.6.8c.4.2.5.4.5.7 0 .7-.4 1.5-.9 1.8-.6.4-1.7.5-3.3-.2-2.5-1-4.7-3.2-5.7-5.7-.6-1.4-.6-2.4-.2-3Z" fill="currentColor" />
          </svg>
        </a>
        <RouteEffects />
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
